import { FormShape, FormUnit, type GetFormInput, type GetFormOutputVerbose } from "@suse-o11y/signal/form";
import { z } from "zod";

import { isStringDirty } from "~/tools/is-string-dirty";

import { FormState as ColorFormState } from "../color/form-state";
import { CRITICAL as CRITICAL_COLOR } from "../color/settings";

import { ALL_MODES, type Mode, type Step as StepSettings } from "./settings";

const modeSchema = z.enum(ALL_MODES).catch(ALL_MODES[0]);

type ModeFormState = FormUnit<string, ReadonlyArray<string>, Mode>;

type ModeInput = GetFormInput<ModeFormState>;

function initMode(initial: ModeInput): ModeFormState {
	return FormUnit(initial, {
		schema: modeSchema,
	});
}

const ModeFormState = {
	init: initMode,
} as const;

type StepColorFormState = ColorFormState;

type StepValueFormState = FormUnit<string, ReadonlyArray<string>, number>;

type StepFormState = FormShape<{
	color: StepColorFormState;
	value: StepValueFormState;
}>;

type StepInput = GetFormInput<StepFormState>;

const INITIAL_STEP = {
	color: CRITICAL_COLOR,
	value: "",
} satisfies StepInput;

function initStep(initial: StepInput): StepFormState {
	return FormShape({
		color: ColorFormState.init(initial.color),
		value: FormUnit(initial.value, {
			isInputDirty: isStringDirty,
			schema: z.coerce.number(),
		}),
	});
}

function fromStepSettings(step: undefined | StepSettings): StepInput {
	return {
		value: step?.value?.toString() ?? INITIAL_STEP.value,
		color: step?.color ?? INITIAL_STEP.color,
	};
}

function toStepSettings(output: GetFormOutputVerbose<StepFormState>): StepSettings {
	return {
		value: output.value ?? undefined,
		color: output.color ?? undefined,
	};
}

function toStepSync({ value, color }: StepInput): StepInput {
	return {
		value: value ?? INITIAL_STEP.value,
		color: color ?? INITIAL_STEP.color,
	};
}

const StepFormState = {
	initial: INITIAL_STEP,
	init: initStep,
	fromSettings: fromStepSettings,
	toSettings: toStepSettings,
	toSync: toStepSync,
} as const;

export { StepFormState, ModeFormState };
export type { StepColorFormState, StepValueFormState };
